// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useCallback, useEffect, useRef, Fragment } from 'react';
import '@atlaskit/css-reset';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import AppStyle from '@atlassian/jira-common-components-app-style/src/index.tsx';
import LoginRedirect from '@atlassian/jira-common-navigation/src/login-redirect/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import type { FetchTabDataFn } from '@atlassian/jira-home/src/common/types/fetch.tsx';
import {
	fetchData as assignedToUserFetchData,
	transformResult as transformAssignedToUserResult,
} from '@atlassian/jira-home/src/services/assigned-to-user-provider/index.tsx';
import {
	fetchData as favouritesFetchData,
	transformResult as transformFavouritesResult,
} from '@atlassian/jira-home/src/services/favourites-provider/index.tsx';
import { transformResult as transformRecentActivitiesResult } from '@atlassian/jira-home/src/services/recent-activity-provider/index.tsx';
import {
	fetchAllData as recentlyViewedFetchData,
	transformResult as transformRecentlyViewedResult,
} from '@atlassian/jira-home/src/services/recently-viewed-provider/index.tsx';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { withSuspense } from '@atlassian/jira-platform-resources-utils/src/utils/with-suspense/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { jiraHomeResourceWithCache } from '@atlassian/jira-router-resources-jira-home/src/index.tsx';
import { HrExpansionSpotlight } from '@atlassian/jira-service-management-hr-expansion-experiment-spotlight/src/index.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { generateUpdateTabStateFn } from './utils.tsx';
import JiraHome from './view/view.tsx';

const useJiraHomePrefetchResource = withSuspense((intl: IntlShape) => {
	const assignedToUserLoading = useRef(false);

	const state = useResource(jiraHomeResourceWithCache);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const generateUpdateTabFn = useCallback(
		(tabName: string, fetchData: FetchTabDataFn) =>
			generateUpdateTabStateFn(state, tabName, fetchData),
		[state],
	);

	const updateLastVisitedTab = useCallback(
		(lastVisitedTab: string) => {
			state.update(
				(currentData) =>
					currentData && {
						...currentData,
						lastVisitedTab,
					},
			);
		},
		[state],
	);

	useEffect(() => {
		if (
			state?.data?.tabs.AssignedToUser?.data?.issues?.totalCount == null &&
			!assignedToUserLoading.current
		) {
			assignedToUserLoading.current = true;
			assignedToUserFetchData().then((assignedToUserResults) => {
				state.update(
					(currentData) =>
						currentData && {
							...currentData,
							tabs: {
								...currentData.tabs,
								AssignedToUser: assignedToUserResults,
							},
						},
				);
			});
		}
	}, [state]);

	useEffect(() => {
		if (state?.data?.analytics) {
			fireUIAnalytics(
				createAnalyticsEvent({ action: 'load', actionSubject: 'jiraHome' }),
				'jiraHomeResource',
				state.data.analytics,
			);
		}
	}, [createAnalyticsEvent, state?.data?.analytics]);

	try {
		if (state.data) {
			const { RecentlyViewed, AssignedToUser, Favourites, WorkedOn } = state.data.tabs;

			const tabs = {
				RecentlyViewed: {
					data: transformRecentlyViewedResult(RecentlyViewed, intl),
					timeZone: RecentlyViewed?.timeZone,
					update: generateUpdateTabFn('RecentlyViewed', recentlyViewedFetchData),
				},

				AssignedToUser: {
					data: transformAssignedToUserResult(AssignedToUser),
					update: generateUpdateTabFn('AssignedToUser', assignedToUserFetchData),
				},

				Favourites: {
					data: transformFavouritesResult(Favourites),
					update: generateUpdateTabFn('Favourites', favouritesFetchData),
				},
				WorkedOn: {
					data: transformRecentActivitiesResult(WorkedOn, intl),
				},
			};

			return {
				loading: state.loading,
				refresh: state.refresh,
				promise: state.promise,
				error: state.error,
				data: {
					lastVisitedTab: (state.data && state.data.lastVisitedTab) || '',
					tabs,
					updateLastVisitedTab,
					recommendedProjectsData: expValEquals(
						'recommended_projects_for_you_page',
						'cohort',
						'test',
						'control',
					)
						? state.data.recommendedProjectsData
						: undefined,
				},
			};
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		log.safeErrorWithoutCustomerData('spa-apps.jira-home', 'Cannot prefetch the data', e);
	}

	return {
		loading: state.loading,
		refresh: state.refresh,
		promise: state.promise,
		error: state.error,
		data: null,
	};
});

export const JiraHomeShell = (): React.JSX.Element => {
	const { atlassianAccountId } = useTenantContext();
	const intl = useIntl();
	const state = useJiraHomePrefetchResource(intl);

	if (!atlassianAccountId) {
		return <LoginRedirect />;
	}

	return (
		<UFOSegment name="your-work">
			<AppBase id="jira-home">
				<AppStyle>
					{/* @ts-expect-error - TS2322 - Type '{ loading: boolean; promise: Promise<JiraHomeRouteResourceData | null> | null; error: Error | Record<string, any> | (Error & Record<...>) | (Record<...> & Error) | null; data: { ...; }; } | { ...; }' is not assignable to type 'Partial<RouteResourceResponse<PrefetchResult>>'. */}
					<JiraHome prefetchResult={state} />
				</AppStyle>
			</AppBase>
		</UFOSegment>
	);
};

const JiraHomeType = (): React.JSX.Element => (
	<>
		<MarkProductStart />
		<JiraHomeShell />
		{expVal('jsm_hr_expansion', 'cohort', 'not-enrolled') !== 'not-enrolled' ? (
			<HrExpansionSpotlight />
		) : null}
	</>
);

export default JiraHomeType;
